/**
 * @module Auth0HttpHelper
 * @description A generic, static XHR-based REST helper class
 */
/* eslint-disable import/extensions */
import Auth0HttpError from './auth0-localized-error.mjs';
import Auth0Constants from './auth0-constants.mjs';
import logger from './logging/auth0-logger.mjs';

class Auth0HttpHelper {
  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `GET` request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `GET` request. Said promise resolves to a string representing the server response
   */
  static get = async (options) => {
    const localizationFailureContext = options.contextKey || 'error-fetch-generic';
    options.contextKey = localizationFailureContext;
    options.verb = 'GET';
    return this._request(options);
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `GET` request expecting a JSON response
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the `GET` request. Said promise resolves to a parsed object representing the server response
   */
  static getJson = async (options) => {
    options.headers = options.headers || {};
    options.headers.Accept = 'application/json';
    const response = await Auth0HttpHelper.get(options);
    return typeof response === 'string'
      ? JSON.parse(response)
      : {};
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `POST` request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {(string|object)} [options.body] Optional: The POST body. Can be a JSON string or an object
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `POST` request. Said promise resolves to a string representing the server response
   */
  static post = async (options) => {
    const localizationFailureContext = options.contextKey || 'error-post-generic';
    options.headers = options.headers || {};
    options.headers['Content-type'] = 'application/json';
    options.contextKey = localizationFailureContext;
    options.verb = 'POST';
    return this._request(options);
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `POST` request expecting a JSON response
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {(string|object)} [options.body] Optional: The POST body. Can be a JSON string or an object
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|object)} A promise object whose resolution represents completion of the `POST` request. Said promise resolves to a parsed object representing the server response
   */
  static postJson = async (options) => {
    options.headers = options.headers || {};
    options.headers.Accept = 'application/json';
    const response = await Auth0HttpHelper.post(options);
    return typeof response === 'string'
      ? JSON.parse(response)
      : {};
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `POST` request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {(string|object)} [options.body] Optional: The POST body. Can be a JSON string or an object
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `POST` request. Said promise resolves to a string representing the server response
   */
  static postData = async (options) => {
    const localizationFailureContext = options.contextKey || 'error-post-generic';
    options.headers = options.headers || {};
    options.headers['Content-type'] = 'application/x-www-form-urlencoded';
    options.contextKey = localizationFailureContext;
    options.verb = 'POST';
    return this._request(options);
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `POST` request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {(string|object)} [options.body] Optional: The POST body. Can be a JSON string or an object
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `POST` request. Said promise resolves to a string representing the server response
   */
  static postDataJson = async (options) => {
    const response = await this.postData(options);
    return typeof response === 'string'
      ? JSON.parse(response)
      : {};
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `DELETE` request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `DELETE` request. Said promise resolves to a string representing the server response
   */
  static delete = async (options) => {
    const localizationFailureContext = options.contextKey || 'error-delete-generic';
    options.contextKey = localizationFailureContext;
    options.verb = 'DELETE';
    return this._request(options);
  };

  /**
   * @function
   * @async
   * @description A wrapper around an XHR-based `DELETE` request expecting a JSON response
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {string} [options.contextKey] Optional: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the `DELETE` request. Said promise resolves to a parsed object representing the server response
   */
  static deleteJson = async (options) => {
    options.headers = options.headers || {};
    options.headers.Accept = 'application/json';
    options.headers['Content-type'] = 'application/json';
    const response = await Auth0HttpHelper.delete(options);
    return typeof response === 'string'
      ? JSON.parse(response)
      : {};
  };

  /**
   * @function
   * @async
   * @private
   * @description An internal wrapper around an XHR-based request
   * @param {object} options The request options
   * @param {string} options.url Required: The URL to which the request should be made
   * @param {string} options.verb Required: The HTTP verb (GET, POST, DELETE, etc)
   * @param {string} options.contextKey Required: The context key for the request. In the event of an error, this key will be used to lookup and provide a localized error ({@link module:Auth0LanguagePacks|Auth0LanguagePacks})
   * @param {(string|object)} [options.body] Optional: The POST body. Can be a JSON string or an object
   * @param {string} [options.jwt] Optional: The JWT bearer token for the request's Authorization header
   * @param {object} [options.headers] Optional: Key-value pair object for HTTP headers to be added to the request
   * @param {boolean} [options.withCredentials] Optional: Whether to send credentials (cookies) with the request. Defaults to true for same-origin requests. Set to false for cross-origin requests to third-party APIs like Auth0 Management API.
   * @returns {(Promise|string)} A promise object whose resolution represents completion of the verb's request. Said promise resolves to a string representing the server response
   */
  static _request = async (options) => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      const buildHttpError = () => {
        const httpError = new Auth0HttpError(`Failed with status ${xhr.status} (HTTP ${options.verb} ${options.url})`, options.contextKey, Auth0Constants.ERROR_CODE_FAIL_XHR_ERROR);
        httpError.response = {
          request: {
            verb: options.verb,
            url: options.url,
          },
          status: xhr.status,
          data: xhr.responseText,
        };

        if (httpError.response.data) {
          try {
            httpError.response.dataObj = JSON.parse(httpError.response.data);
          } catch (error) {
            // not json
            httpError.response.dataObj = undefined;
          }
        }
        return httpError;
      };
      xhr.open(options.verb, options.url);
      xhr.withCredentials = options.withCredentials ?? true;
      if (options.jwt) {
        xhr.setRequestHeader('Authorization', `Bearer ${options.jwt}`);
      }
      if (options.headers) {
        Object.keys(options.headers).forEach((headerKey) => {
          xhr.setRequestHeader(headerKey, options.headers[headerKey]);
        });
      }
      xhr.onload = () => {
        if (!!xhr.status && xhr.status < 400) {
          console.log(`Auth0HttpHelper ${options.verb} @ ${options.url} success! response: ${xhr.responseText}`);
          resolve(xhr.responseText);
          return;
        }
        if (!!xhr.status && xhr.status === 401) {
          console.log(`Auth0HttpHelper ${options.verb} @ ${options.url} 401! response: ${xhr.responseText}`);
        } else {
          logger.error(`Auth0HttpHelper ${options.verb} @ ${options.url} has failed! response: ${xhr.responseText}`);
        }
        reject(buildHttpError());
      };
      xhr.onerror = (e) => {
        if (!!xhr.status && xhr.status === 401) {
          console.log(`Auth0HttpHelper ${options.verb} @ ${options.url} 401!`, e);
        } else {
          logger.error(`Auth0HttpHelper ${options.verb} @ ${options.url} has failed! Error: ${e}`);
        }
        reject(buildHttpError());
      };

      if (options.body) {
        options.body = typeof options.body === 'string'
          ? options.body
          : JSON.stringify(options.body);
        xhr.send(options.body);
      } else {
        xhr.send();
      }
    });
  };
}

export default Auth0HttpHelper;
